import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import CmsPageLayout from "../components/CmsPageLayout";
import CmsPageRoot from "../components/CmsPageRoot";
import CmsPageShell from "../components/CmsPageShell";
import ValidationSummary from "../components/ValidationSummary";
import StickyFormActions from "../components/StickyFormActions";
import PreviewJsonModal from "../components/PreviewJsonModal";
import { CMS_LANGUAGES, CMS_PAGE_META, DEFAULT_HOME, HOME_SECTIONS } from "../constants";
import { validateHomePageBilingual } from "../utils/validation";
import { normalizeHomeSectionIcons } from "../utils/cmsImageAsset";
import { applyCmsValidationFailure, scrollToFirstValidationError } from "../utils/validationUi";
import { fetchHomeContent, saveHomeContent, uploadMedia } from "../api/cmsApi";
import { useCmsAuth } from "../hooks/useCmsAuth";
import {
  HeroSection,
  TrustBarSection,
  CareChallengeSection,
  FeaturesSection,
  HowItWorksSection,
  RoleExperienceSection,
  SecuritySection,
} from "../sections/HomeSections";

const PAGE_META = CMS_PAGE_META.home;
const EN_ID = CMS_LANGUAGES.en.id;
const ES_ID = CMS_LANGUAGES.es.id;

const SECTION_PREFIX_MAP = [
  { id: "hero", prefixes: ["hero."] },
  { id: "trustBar", prefixes: ["trustBar."] },
  { id: "careChallenge", prefixes: ["careChallenge."] },
  { id: "features", prefixes: ["features."] },
  { id: "howItWorks", prefixes: ["howItWorks."] },
  { id: "roleExperience", prefixes: ["roleExperience."] },
  { id: "security", prefixes: ["security."] },
];

const cloneHome = () => JSON.parse(JSON.stringify(DEFAULT_HOME));

function HomePageEditor() {
  const { token } = useCmsAuth();
  const [homeEn, setHomeEn] = useState(() => cloneHome());
  const [homeEs, setHomeEs] = useState(() => cloneHome());
  const [initialEn, setInitialEn] = useState(() => cloneHome());
  const [initialEs, setInitialEs] = useState(() => cloneHome());
  const [updatedAtEn, setUpdatedAtEn] = useState(null);
  const [updatedAtEs, setUpdatedAtEs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [activeAnchor, setActiveAnchor] = useState("hero");

  const dirtyEn = JSON.stringify(homeEn) !== JSON.stringify(initialEn);
  const dirtyEs = JSON.stringify(homeEs) !== JSON.stringify(initialEs);
  const dirty = dirtyEn || dirtyEs;

  const loadAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErrors({});
    try {
      const [enResult, esResult] = await Promise.all([
        fetchHomeContent(token, EN_ID),
        fetchHomeContent(token, ES_ID),
      ]);
      const enMerged = normalizeHomeSectionIcons({ ...cloneHome(), ...(enResult.home || {}) });
      const esMerged = normalizeHomeSectionIcons({ ...cloneHome(), ...(esResult.home || {}) });
      setHomeEn(enMerged);
      setHomeEs(esMerged);
      setInitialEn(JSON.parse(JSON.stringify(enMerged)));
      setInitialEs(JSON.parse(JSON.stringify(esMerged)));
      setUpdatedAtEn(enResult.updatedAt);
      setUpdatedAtEs(esResult.updatedAt);
    } catch (err) {
      const fallback = cloneHome();
      setHomeEn(fallback);
      setHomeEs(cloneHome());
      setInitialEn(JSON.parse(JSON.stringify(fallback)));
      setInitialEs(JSON.parse(JSON.stringify(cloneHome())));
      toast.error("Could not load from server. Restore CMS data in MongoDB.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const scrollToSection = (sectionId) => {
    setActiveAnchor(sectionId);
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSave = async () => {
    const validationErrors = validateHomePageBilingual(homeEn, homeEs);
    if (Object.keys(validationErrors).length) {
      applyCmsValidationFailure({
        validationErrors,
        setErrors,
        sectionPrefixMap: SECTION_PREFIX_MAP,
        onSectionChange: setActiveAnchor,
        toastFn: (message) => toast.error(message),
      });
      return;
    }

    setSaving(true);
    try {
      const [enResult, esResult] = await Promise.all([
        saveHomeContent(token, EN_ID, homeEn),
        saveHomeContent(token, ES_ID, homeEs),
      ]);
      setInitialEn(JSON.parse(JSON.stringify(homeEn)));
      setInitialEs(JSON.parse(JSON.stringify(homeEs)));
      setErrors({});
      setUpdatedAtEn(enResult.updatedAt || new Date().toISOString());
      setUpdatedAtEs(esResult.updatedAt || new Date().toISOString());
      toast.success("Saved — live on the website (English & Spanish).");
    } catch (err) {
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (lang, key, file, onSuccess) => {
    setUploadingKey(`${lang}.${key}`);
    try {
      const publicUrl = await uploadMedia(token, file);
      if (publicUrl) {
        onSuccess(publicUrl);
        toast.success("Image uploaded.");
      } else {
        toast.error("No URL returned from upload.");
      }
    } catch (err) {
      toast.error("Upload failed.");
    } finally {
      setUploadingKey(null);
    }
  };

  const sectionProps = {
    homeEn,
    homeEs,
    setHomeEn,
    setHomeEs,
    errors,
    onImageUpload: handleImageUpload,
    uploadingKey,
  };

  return (
    <CmsPageRoot>
      <CmsPageLayout
        toolbar={
          !loading ? (
            <nav className="cms-sticky-nav" aria-label="Sections">
              {HOME_SECTIONS.map((section, index) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className={activeAnchor === section.id ? "active" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(section.id);
                  }}
                >
                  {index + 1}. {section.label}
                </a>
              ))}
            </nav>
          ) : null
        }
        actions={
          <StickyFormActions
            dirty={dirty}
            saving={saving}
            onSave={handleSave}
            onPreview={() => setPreviewOpen(true)}
          />
        }
      >
      <CmsPageShell
        pageIcon={PAGE_META.pageIcon}
        pageIconSrc={PAGE_META.tabIcon}
        title={PAGE_META.title}
        subtitle={PAGE_META.subtitle}
        dirtyEn={dirtyEn}
        dirtyEs={dirtyEs}
        updatedAtEn={updatedAtEn}
        updatedAtEs={updatedAtEs}
        loading={loading}
        skeletonPage="home"
      >
        <ValidationSummary
          errors={errors}
          onJumpToFirst={() =>
            scrollToFirstValidationError(errors, {
              sectionPrefixMap: SECTION_PREFIX_MAP,
              onSectionChange: setActiveAnchor,
            })
          }
        />

        <HeroSection {...sectionProps} />
        <TrustBarSection {...sectionProps} />
        <CareChallengeSection {...sectionProps} />
        <FeaturesSection {...sectionProps} />
        <HowItWorksSection {...sectionProps} />
        <RoleExperienceSection {...sectionProps} />
        <SecuritySection {...sectionProps} />
      </CmsPageShell>
      </CmsPageLayout>

      <PreviewJsonModal
        isOpen={previewOpen}
        toggle={() => setPreviewOpen(false)}
        title="Home page payload"
        payload={{ en: { home: homeEn }, es: { home: homeEs } }}
      />
    </CmsPageRoot>
  );
}

export default HomePageEditor;
